import { Link } from "wouter";
import { Building2, ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo-head";

const REFERRAL_TERMS_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Referral Policy",
    "description": "The Proper Tool referral programme terms: how to earn rewards by referring new subscribers to the platform.",
    "url": "https://thepropertool.com/referral-terms",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Referral Policy",
        "item": "https://thepropertool.com/referral-terms",
      },
    ],
  },
];

export default function ReferralTermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Referral Policy"
        description="The Proper Tool referral programme terms: how to earn rewards by referring new subscribers to the platform."
        canonical="/referral-terms"
        schema={REFERRAL_TERMS_SCHEMA}
      />
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">The Proper Tool</span>
            </div>
          </Link>
          <Link href="/">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Referral Programme Terms</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: March 2026</p>

        <div className="prose prose-sm max-w-none space-y-8 text-foreground">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. About the Referral Programme</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool operates a referral programme that allows active subscribers to earn fixed monthly rewards by referring new paying subscribers to the platform. By participating in the referral programme, you agree to these terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed">To take part in the referral programme, you must:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>Hold an active paid subscription to The Proper Tool (Lite, Starter or Pro)</li>
              <li>Be in good standing with your account (no outstanding payments, no policy violations)</li>
              <li>Use your unique referral code, which is generated automatically when you subscribe</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Trial users can share their referral code, but rewards will only begin once the referring user has an active paid subscription.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. How It Works</h2>
            <p className="text-muted-foreground leading-relaxed">
              Each subscriber is assigned a unique referral code. When a new user signs up using your code and subscribes to a paid plan, you earn a fixed monthly reward for as long as their subscription remains active, up to a maximum of 12 months.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Referral codes must be entered by the new user before they subscribe. Codes cannot be applied retrospectively after a subscription has started.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Reward Amounts</h2>
            <p className="text-muted-foreground leading-relaxed">Fixed monthly rewards are based on the plan the referred user subscribes to:</p>

            <div className="mt-4 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left px-4 py-3 font-medium text-foreground">Plan</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground">Monthly Reward</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground">Max Duration</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground">Max Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Lite (£29/month)</td>
                    <td className="px-4 py-3 text-muted-foreground font-medium text-foreground">£10/month</td>
                    <td className="px-4 py-3 text-muted-foreground">12 months</td>
                    <td className="px-4 py-3 text-muted-foreground">£120</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Starter (£59/month)</td>
                    <td className="px-4 py-3 text-muted-foreground font-medium text-foreground">£15/month</td>
                    <td className="px-4 py-3 text-muted-foreground">12 months</td>
                    <td className="px-4 py-3 text-muted-foreground">£180</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Pro (£99/month)</td>
                    <td className="px-4 py-3 text-muted-foreground font-medium text-foreground">£20/month</td>
                    <td className="px-4 py-3 text-muted-foreground">12 months</td>
                    <td className="px-4 py-3 text-muted-foreground">£240</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-muted-foreground leading-relaxed mt-4">
              Rewards are based on the referred user's plan at the time of payment. If they change plan, rewards will adjust accordingly from the next billing cycle.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. When Rewards Stop</h2>
            <p className="text-muted-foreground leading-relaxed">Rewards will stop in the following circumstances:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>The referred subscriber cancels their subscription</li>
              <li>The referred subscriber's payment lapses or fails and is not resolved</li>
              <li>12 months have passed since the referred subscriber first activated their paid plan</li>
              <li>The referring user's account is suspended or terminated for breach of these terms</li>
              <li>We suspend or close the referral programme</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Reward Payments</h2>
            <p className="text-muted-foreground leading-relaxed">
              Rewards accumulate in your in-app wallet. You may request a withdrawal once your balance reaches the minimum withdrawal threshold. Withdrawals are paid by bank transfer to the UK bank account you provide.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Referral rewards may be subject to income tax depending on your personal tax circumstances. You are responsible for declaring any earnings to HMRC. We do not deduct tax at source.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Fraud Prevention</h2>
            <p className="text-muted-foreground leading-relaxed">
              The referral programme is intended for genuine referrals only. The following activities are strictly prohibited:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>Referring yourself using a second account</li>
              <li>Creating fake accounts to generate referral rewards</li>
              <li>Using automated tools to generate referrals</li>
              <li>Any form of misrepresentation when promoting the platform</li>
              <li>Sharing misleading or false information to encourage sign-ups</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We monitor referral activity for signs of abuse. If we detect fraudulent activity, we reserve the right to withhold or reverse rewards, suspend your account, and pursue any losses through legal means.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Changes to the Programme</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify, pause or close the referral programme at any time. We will give reasonable notice of changes where possible. Changes will not affect rewards already accrued and approved before the change takes effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. General</h2>
            <p className="text-muted-foreground leading-relaxed">
              These referral programme terms form part of the overall Terms and Conditions of The Proper Tool. In the event of any conflict, the main Terms and Conditions shall take precedence. These terms are governed by the laws of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For any questions about the referral programme, please contact hello@thepropertool.com.
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t mt-16 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} The Proper Tool. All rights reserved.</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link href="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms and Conditions</Link>
            <Link href="/referral-terms" className="hover:text-foreground transition-colors">Referral Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
